import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../../Security/firebase-config';
import {
  collection,
  getDocs,
  doc,
} from 'firebase/firestore';
import { useTeam } from '../../context/TeamContext';
import FocusPoints from './FocusPoints';
import {ArrowLeft} from 'lucide-react';
import NotesDisplay from './NotesDisplay';
import ReviewedButton from './ReviewedButton';
import CustomAlert from '../core-components/CustomAlert';


import "./EndOfReviewSummary.css";

export default function EndOfReviewSummary({ 
  mapId,
  videoId,
  mapName,
  videoTitle,
  onBackToMaps,
  onBackToVideos,
  onResumeReview,
  showFocusOutcomes = true,
}) {
  // refs to detect clicks outside our two dropdown wrappers
  const filterContainerRef = useRef(null);
  const compareContainerRef = useRef(null);

  const { teamId, username } = useTeam();
  const [focusPoints, setFocusPoints] = useState([]);
  const [allNotes, setAllNotes] = useState([]);
  const [filterMentions, setFilterMentions] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [filterImportance, setFilterImportance] = useState([]);
  const [hideReviewedByMe, setHideReviewedByMe] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const [comparisonVideoTitle, setComparisonVideoTitle] = useState('');
  const [comparisonVideoId, setComparisonVideoId] = useState(null);
  const [comparisonNotes, setComparisonNotes] = useState([]);
  const [mapVideoList, setMapVideoList] = useState([]);
  const [showCompareDropdown, setShowCompareDropdown] = useState(false);
  const [customAlert, setCustomAlert] = useState(null);



  // Fetch all notes and focus points from Firestore
  useEffect(() => {
    if (!teamId || !mapId || !videoId) return;

    const fetchData = async () => {
      const videoDoc = doc(db, 'teams', teamId, 'maps', mapId, 'videos', videoId);
      const fpSnap = await getDocs(collection(videoDoc, 'focusPoints'));
      const noteSnap = await getDocs(collection(videoDoc, 'notes'));

      setFocusPoints(fpSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setAllNotes(
        noteSnap.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            at: Array.isArray(data.at) ? data.at : [],
            tags: Array.isArray(data.tags) ? data.tags : [],
            importance: typeof data.importance === 'number' ? data.importance : 0,
            reviewedBy: Array.isArray(data.reviewedBy) ? data.reviewedBy : [],
            text: typeof data.text === 'string' ? data.text : '',
            content: typeof data.content === 'string' ? data.content : ''
          };
        })
      );
    };
    fetchData();

    // Fetch all videos for this map to populate the comparison dropdown
    const fetchAllVideosForMap = async () => {
      const videoSnap = await getDocs(collection(db, 'teams', teamId, 'maps', mapId, 'videos'));
      const videos = videoSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(v => v.id !== videoId); // Exclude the current video
      setMapVideoList(videos);
    };

    fetchAllVideosForMap();


  }, [mapId, videoId, teamId]);

  // Filter settings
  const filters = React.useMemo(() => ({
    filterMentions: filterMentions || [],
    filterImportance: filterImportance || [],
    filterTags: filterTags || [],
    hideReviewedByMe: !!hideReviewedByMe,
    currentUser: username,
  }), [filterMentions, filterImportance, filterTags, hideReviewedByMe, username]);

  // Apply filters to the notes
  const filteredNotes = React.useMemo(() => {
    return allNotes.filter((note) => {
      const {
        filterMentions,
        filterImportance,
        filterTags,
        hideReviewedByMe,
        currentUser,
      } = filters;

      // 1) FILTER BY MENTIONS + “team” logic
      if (filterMentions.length > 0) {
        const noteAtNormalized = (note.at || [])
          .map(m => m.toLowerCase().replace(/^@/, ""));
        const includesTeamOnly =
          filterMentions.length === 1 && filterMentions[0] === "team";
        const matchesAny = filterMentions.some(m => noteAtNormalized.includes(m));
        const hasTeamTag = noteAtNormalized.includes("team");
        if (includesTeamOnly ? !hasTeamTag : (!matchesAny && !hasTeamTag)) {
          return false;
        }
      }

      // 2) FILTER BY IMPORTANCE
      if (filterImportance.length > 0) {
        if (!filterImportance.includes(note.importance)) {
          return false;
        }
      }

      // 3) FILTER BY TAGS
      if (filterTags.length > 0) {
        const lowerTags = (note.tags || []).map((t) => t.toLowerCase());
        const hasAnyTag = filterTags.some((ft) =>
          lowerTags.includes(ft.toLowerCase())
        );
        if (!hasAnyTag) {
          return false;
        }
      }

      // 4) FILTER “NOT REVIEWED BY ME”
      if (hideReviewedByMe) {
        const viewers = Array.isArray(note.viewers) ? note.viewers : [];
        if (viewers.includes(currentUser)) return false;
      }

      return true;
    });
  }, [allNotes, filters]);

  // Generate unique mention suggestions
  const mentionOptions = React.useMemo(() => {
    const allAt = allNotes.flatMap((n) => n.at || []);
    const map = new Map();
    allAt.forEach((raw) => {
      const stripped = raw.replace(/^@/, '');
      const lower = stripped.toLowerCase();
      if (!map.has(lower)) {
        map.set(lower, stripped);
      }
    });
    return Array.from(map.entries())
      .filter(([lower]) => lower !== 'team') // Remove 'team' mention
      .map(([_, orig]) => '@' + orig);
  }, [allNotes]);

  // Generate unique tag suggestions
  const tagOptions = React.useMemo(() => {
    const allTags = allNotes.flatMap((n) => n.tags || []);
    const uniq = Array.from(new Set(allTags.map((t) => t.toLowerCase())));
    return uniq;
  }, [allNotes]);

  
  const handleCompareVideoSelect = async (vidId) => {
    setComparisonVideoId(vidId);

    // grab the human title
    const sel = mapVideoList.find(v => v.id === vidId);
    setComparisonVideoTitle(sel?.title || vidId);

    const videoDoc = doc(db, 'teams', teamId, 'maps', mapId, 'videos', vidId);
    const noteSnap = await getDocs(collection(videoDoc, 'notes'));
    const notes = noteSnap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        at: Array.isArray(data.at) ? data.at : [],
        tags: Array.isArray(data.tags) ? data.tags : [],
        importance: typeof data.importance === 'number' ? data.importance : 0,
        reviewedBy: Array.isArray(data.reviewedBy) ? data.reviewedBy : [],
        text: typeof data.text === 'string' ? data.text : '',
        content: typeof data.content === 'string' ? data.content : ''
      };
    });
    setComparisonNotes(notes);
  };

  // ─── click‐outside handler ───────────────────────────────────────
  useEffect(() => {
      const onBodyClick = e => {
        // close filter if open & click outside
        if (showFilterPanel
          && filterContainerRef.current
          && !filterContainerRef.current.contains(e.target)
        ) {
          setShowFilterPanel(false);
        }
        // close compare if open & click outside
        if (showCompareDropdown
          && compareContainerRef.current
          && !compareContainerRef.current.contains(e.target)
        ) {
          setShowCompareDropdown(false);
        }
      };
  
      document.addEventListener('mousedown', onBodyClick);
      return () => document.removeEventListener('mousedown', onBodyClick);
    }, [showFilterPanel, showCompareDropdown]);
  

    /**
     * Notify teammates this VOD is ready (same cloud function used by VideoList)
     */
    const handleNotifyVodReady = async () => {
      try {
        const token = await auth.currentUser.getIdToken(true);
        const url =
          `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/notifyVodReady`;

        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ teamId, mapId, videoId }),
        });

        if (!res.ok) throw new Error(await res.text());
        const data = await res.json(); // { ok, inApp, discord }

        setCustomAlert({
          message: `VOD ready notification sent to the team. (in-app: ${data.inApp ?? 0}, Discord: ${data.discord ?? 0})`,
          type: 'success',
          position: 'top',
        });
      } catch (e) {
        console.error('notify VOD ready failed:', e);
        setCustomAlert({
          message: 'Could not send VOD ready notification.',
          type: 'error',
          position: 'top',
        });
      }
    };

    return (
      <div className="end-review-summary">
        
        


        {/* ─── BREADCRUMB ───────────────────────────────────────────── */}
        <div className="vpwn-breadcrumb">
          <span className="vpwn-breadcrumb-link" onClick={onBackToMaps}>
            Map Selection
          </span>
          <span className="vpwn-breadcrumb-sep">/</span>
          <span className="vpwn-breadcrumb-link" onClick={onBackToVideos}>
            {mapName}
          </span>
          <span className="vpwn-breadcrumb-sep">/</span>
          <span
            className="vpwn-breadcrumb-link"
            onClick={onResumeReview}
          >
            {videoTitle}
          </span>
          <span className="vpwn-breadcrumb-sep">/</span>
          <span className="vpwn-breadcrumb-current">End Of Review</span>

          {/* Right-aligned action cluster in the breadcrumb */}
          <div className="vpwn-breadcrumb-actions">
            {/* This is your existing “eye” control (video-eye-wrapper equivalent) */}
            <div className="video-eye-wrapper">
              <ReviewedButton teamId={teamId} videoId={videoId} mapId={mapId} />
            </div>
            {/* Notify sits just to the right of the eye */}
            <button
              className="notify-btn"
              title="Notify all teammates that this VOD is ready"
              onClick={(e) => {
                e.stopPropagation();
                handleNotifyVodReady();
              }}
              aria-label="Notify all teammates that this VOD is ready"
            >
              📣
            </button>
          </div>
        </div>

        <div className="end-review-summary__heading">
          <h3>End of Review Wrap up</h3>
          {onResumeReview && <button
            type="button"
            className="end-review-summary__resume desktop-secondary-button"
            onClick={onResumeReview}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            <span>Back to VOD review</span>
          </button>}
        </div>
    
        <div className="summary-main-wrapper">
          <div className="left-side">
            <FocusPoints
              showOutcomes={showFocusOutcomes}
              focusPoints={focusPoints}
              mapId={mapId}
              videoId={videoId}
              teamId={teamId}
            />
          </div>
    
          <div className="right-side">
            
          </div>
        </div>
    
        <div className="notes-section-wrapper">
          {/* ─── Controls row: Filter + Compare ────────────────────────── */}
          <div className="notes-header">
            <div className="notes-header-right">
              {/* Filter dropdown */}
              <div
                className="filter-button-wrapper"
                ref={filterContainerRef}
              >
                <button
                  className="mode-btn"
                  onClick={() => setShowFilterPanel(v => !v)}
                >
                  Filter 🔍
                </button>
                {showFilterPanel && (
                  <div className="filter-panel">
                    <div className="filter-panel-header">
                      <span className="filter-title">Filters</span>
                      <button
                        className="Filter-Clear-All-btn"
                        onClick={() => {
                          setFilterMentions([]);
                          setFilterTags([]);
                          setFilterImportance([]);
                          setHideReviewedByMe(false);
                        }}
                      >
                        ✖ Clear All
                      </button>
                    </div>
    
                    <div className="filter-group">
                      <label>Filter by @Mention:</label>
                      <div className="mention-checkboxes">
                        {['@Players', ...mentionOptions].map(mention => {
                          const key = mention.toLowerCase().replace(/^@/, '');
                          const checked = filterMentions.includes(key);
                          return (
                            <label key={mention} className="mention-option" style={mention === '@Players' ? {color: 'var(--color-role-player)'} : undefined}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setFilterMentions([...filterMentions, key]);
                                  } else {
                                    setFilterMentions(filterMentions.filter(m => m !== key));
                                  }
                                }}
                              />
                              {mention}
                            </label>
                          );
                        })}
                      </div>
                    </div>
    
                    <div className="filter-group">
                      <label>Filter by Tags:</label>
                      <div className="tag-checkboxes">
                        {tagOptions.map(tag => {
                          const checked = filterTags.includes(tag);
                          return (
                            <label key={tag} className="tag-option">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setFilterTags([...filterTags, tag]);
                                  } else {
                                    setFilterTags(filterTags.filter(t => t !== tag));
                                  }
                                }}
                              />
                              #{tag}
                            </label>
                          );
                        })}
                      </div>
                    </div>
    
                    <div className="filter-group">
                      <label>Importance:</label>
                      <div className="importance-row">
                        {[1, 2, 3].map(n => (
                          <label key={n}>
                            <input
                              type="checkbox"
                              checked={filterImportance.includes(n)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setFilterImportance([...filterImportance, n]);
                                } else {
                                  setFilterImportance(filterImportance.filter(v => v !== n));
                                }
                              }}
                            />
                            {'!'.repeat(n)}
                          </label>
                        ))}
                      </div>
                    </div>
    
                    <div className="filter-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={hideReviewedByMe}
                          onChange={e => setHideReviewedByMe(e.target.checked)}
                        />
                        Hide notes I’ve already reviewed
                      </label>
                    </div>
                  </div>
                )}
              </div>
    
              {/* Compare dropdown */}
              <div
                className="compare-button-wrapper"
                ref={compareContainerRef}
              >
                <button
                  className="mode-btn"
                  onClick={() => setShowCompareDropdown(v => !v)}
                >
                  Compare to Former Notes 📑
                </button>
                {showCompareDropdown && (
                  <div className="compare-dropdown">
                    <label>Select a previous video:</label>
                    <select
                      defaultValue=""
                      onChange={e => handleCompareVideoSelect(e.target.value)}
                    >
                      <option value="" disabled>Choose video</option>
                      {mapVideoList.map(video => (
                        <option key={video.id} value={video.id}>
                          {video.title || video.id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
    
          {/* ─── Titles row ───────────────────────────────────────────── */}
          <div className="notes-titles-row">
            <h4>📝 All Notes This Session</h4>
            {comparisonVideoId && (
              <h4>📝 Notes from “{comparisonVideoTitle}”</h4>
            )}
          </div>
    
          {/* ─── Side-by-side panels ──────────────────────────────────── */}
          <div className="notes-comparison-wrapper">
            <div className="current-notes">
              <NotesDisplay
                notes={filteredNotes}
                mapId={mapId}
                videoId={videoId}
                currentTime={0}
                onSeek={() => {}}
                displayMode="list"
                isPlaying={false}
                autoScroll={false}
                onDisableAuto={() => {}}
                filters={filters}
              />
            </div>
    
            {comparisonVideoId && (
              <div className="comparison-notes-section">
                <NotesDisplay
                  notes={comparisonNotes}
                  mapId={mapId}
                  videoId={comparisonVideoId}
                  currentTime={0}
                  onSeek={() => {}}
                  displayMode="list"
                  isPlaying={false}
                  autoScroll={false}
                  onDisableAuto={() => {}}
                  filters={filters}
                />
              </div>
            )}
            {customAlert && (
              <CustomAlert
                message={customAlert.message}
                type={customAlert.type}
                position={customAlert.position}
                onClose={() => setCustomAlert(null)}
              />
            )}
          </div>
        </div>
      </div>
    );    
    
}
